<script>
import { mapGetters } from 'vuex';
import UpgradePage from './UpgradePage.vue';
import { frontendURL } from '../../../../helper/URLHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import PortalPopover from '../components/PortalPopover.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import portalMixin from '../mixins/portalMixin';
import AddCategory from '../pages/categories/AddCategory.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AppSidebar from 'dashboard/components/layout/AppSidebar.vue';
import MobileHeader from 'dashboard/components/layout/MobileHeader.vue';
import AccountSelector from 'dashboard/components/layout/sidebarComponents/AccountSelector.vue';
import AddAccountModal from 'dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import NewContactModal from 'dashboard/routes/dashboard/conversation/NewContactModal.vue';
import NewConversationModal from 'dashboard/routes/dashboard/conversation/NewConversationModal.vue';
import NotificationPanel from 'dashboard/routes/dashboard/notifications/components/NotificationPanel.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { getSidebarItems } from 'dashboard/components/layout/config/default-sidebar';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { routesWithPermissions } from 'dashboard/routes';

const CommandBar = () =>
  import('dashboard/routes/dashboard/commands/commandbar.vue');

export default {
  components: {
    AddCategory,
    CommandBar,
    PortalPopover,
    UpgradePage,
    AppSidebar,
    MobileHeader,
    AccountSelector,
    AddAccountModal,
    WootKeyShortcutModal,
    NewContactModal,
    NewConversationModal,
    NotificationPanel,
    AddLabelModal,
  },
  mixins: [portalMixin],
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();

    return {
      uiSettings,
      updateUISettings,
    };
  },
  data() {
    return {
      showPortalPopover: false,
      showAddCategoryModal: false,
      lastActivePortalSlug: '',
      showAccountModal: false,
      showCreateAccountModal: false,
      showAddLabelModal: false,
      showShortcutModal: false,
      isNotificationPanel: false,
      showNewConversationModal: false,
      showNewContactModal: false,
      isSidebarOpen: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      portals: 'portals/allPortals',
      categories: 'categories/allCategories',
      meta: 'portals/getMeta',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
    }),

    isHelpCenterEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.HELP_CENTER
      );
    },
    selectedPortal() {
      const slug = this.$route.params.portalSlug || this.lastActivePortalSlug;
      if (slug) return this.$store.getters['portals/portalBySlug'](slug);

      return this.$store.getters['portals/allPortals'][0];
    },
    selectedLocaleInPortal() {
      return this.$route.params.locale || this.defaultPortalLocale;
    },
    selectedPortalName() {
      return this.selectedPortal ? this.selectedPortal.name : '';
    },
    selectedPortalSlug() {
      return this.selectedPortal ? this.selectedPortal?.slug : '';
    },
    defaultPortalLocale() {
      return this.selectedPortal
        ? this.selectedPortal?.meta?.default_locale
        : '';
    },
    accessibleMenuItems() {
      if (!this.selectedPortal) return [];

      const {
        allArticlesCount,
        mineArticlesCount,
        draftArticlesCount,
        archivedArticlesCount,
      } = this.meta;

      return [
        {
          icon: 'book',
          label: 'HELP_CENTER.ALL_ARTICLES',
          key: 'list_all_locale_articles',
          count: allArticlesCount,
          toState: frontendURL(
            `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/${this.selectedLocaleInPortal}/articles`
          ),
          toolTip: 'All Articles',
          toStateName: 'list_all_locale_articles',
        },
        {
          icon: 'pen',
          label: 'HELP_CENTER.MY_ARTICLES',
          key: 'list_mine_articles',
          count: mineArticlesCount,
          toState: frontendURL(
            `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/${this.selectedLocaleInPortal}/articles/mine`
          ),
          toolTip: 'My articles',
          toStateName: 'list_mine_articles',
        },
        {
          icon: 'draft',
          label: 'HELP_CENTER.DRAFT',
          key: 'list_draft_articles',
          count: draftArticlesCount,
          toState: frontendURL(
            `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/${this.selectedLocaleInPortal}/articles/draft`
          ),
          toolTip: 'Draft',
          toStateName: 'list_draft_articles',
        },
        {
          icon: 'archive',
          label: 'HELP_CENTER.ARCHIVED',
          key: 'list_archived_articles',
          count: archivedArticlesCount,
          toState: frontendURL(
            `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/${this.selectedLocaleInPortal}/articles/archived`
          ),
          toolTip: 'Archived',
          toStateName: 'list_archived_articles',
        },
        {
          icon: 'settings',
          label: 'HELP_CENTER.SETTINGS',
          key: 'edit_portal_information',
          toState: frontendURL(
            `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/edit`
          ),
          toStateName: 'edit_portal_information',
        },
      ];
    },
    helpCenterCategories() {
      if (!this.selectedPortal) return [];
      return [
        {
          icon: 'folder',
          label: 'HELP_CENTER.CATEGORY',
          hasSubMenu: true,
          showNewButton: true,
          key: 'category',
          children: this.categories.map(category => ({
            id: category.id,
            label: category.icon
              ? `${category.icon} ${category.name}`
              : category.name,
            count: category.meta.articles_count,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/portals/${this.selectedPortalSlug}/${category.locale}/categories/${category.slug}`
            ),
          })),
        },
      ];
    },
    headerTitle() {
      return this.selectedPortal ? this.selectedPortal.name : '';
    },

    // From Dashboard.vue
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        if (
          menuItem.key === 'contacts' &&
          this.currentRole === 'agent' &&
          this.hideContactsForAgents
        ) {
          return false;
        }
        const isAvailableForTheUser = hasPermissions(
          routesWithPermissions[menuItem.toStateName],
          userPermissions
        );

        if (!isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
    secondaryMenuItems() {
      if (this.activePrimaryMenu.key === 'help_center') {
        return [...this.accessibleMenuItems, ...this.helpCenterCategories];
      }

      if (!this.activeSecondaryMenu || !this.activeSecondaryMenu.menuItems) {
        return [];
      }

      const accessibleItems = this.activeSecondaryMenu.menuItems.filter(
        menuItem => {
          if (menuItem.showModalForNewItem) return true;
          const userPermissions = getUserPermissions(
            this.currentUser,
            this.accountId
          );
          const hasFeatureFlag = menuItem.featureFlag
            ? this.isFeatureEnabledonAccount(
                this.accountId,
                menuItem.featureFlag
              )
            : true;
          const hasRequiredPermissions = hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );
          return hasFeatureFlag && hasRequiredPermissions;
        }
      );

      const additionalItems =
        this.additionalSecondaryMenuItems[this.activeSecondaryMenu.parentNav] ||
        [];
      return [...accessibleItems, ...additionalItems];
    },
    additionalSecondaryMenuItems() {
      const conversationMenuItems = [this.inboxSection(), this.labelSection()];
      if (this.teams.length) {
        conversationMenuItems.unshift(this.teamSection());
      }
      if (this.customViews.length) {
        conversationMenuItems.unshift(this.filtersSection());
      }

      const contactMenuItems = [this.contactLabelSection()];
      if (this.contactCustomViews.length) {
        contactMenuItems.unshift(this.contactSegmentsSection());
      }

      return {
        conversations: conversationMenuItems.filter(
          item => item && Object.keys(item).length
        ),
        contacts: contactMenuItems.filter(
          item => item && Object.keys(item).length
        ),
      };
    },
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    hideContactsForAgents() {
      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          'hide_contacts_for_agent'
        ) && this.currentRole !== 'administrator'
      );
    },
    hideAllInboxForAgents() {
      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          'hide_all_inbox_for_agent'
        ) && this.currentRole !== 'administrator'
      );
    },
  },

  watch: {
    $route() {
      this.isSidebarOpen = false;

      const routeName = this.$route?.name;
      const routeParams = this.$route?.params;
      const updateMetaInAllPortals = routeName === 'list_all_portals';
      const updateMetaInEditArticle =
        routeName === 'edit_article' && routeParams?.recentlyCreated;
      const updateMetaInLocaleArticles =
        routeName === 'list_all_locale_articles' &&
        routeParams?.recentlyDeleted;
      if (
        updateMetaInAllPortals ||
        updateMetaInEditArticle ||
        updateMetaInLocaleArticles
      ) {
        this.fetchPortalAndItsCategories();
      }
    },
    activeCustomView() {
      this.fetchCustomViews();
    },
  },

  mounted() {
    this.$emitter.on(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);

    const slug = this.$route.params.portalSlug;
    if (slug) this.lastActivePortalSlug = slug;

    this.fetchPortalAndItsCategories();

    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
  },
  beforeDestroy() {
    this.$emitter.off(BUS_EVENTS.TOGGLE_SIDEMENU, this.toggleSidebar);
  },
  updated() {
    const slug = this.$route.params.portalSlug;
    if (slug !== this.lastActivePortalSlug) {
      this.lastActivePortalSlug = slug;
      this.updateUISettings({
        last_active_portal_slug: slug,
        last_active_locale_code: this.selectedLocaleInPortal,
      });
    }
  },
  methods: {
    async fetchPortalAndItsCategories() {
      await this.$store.dispatch('portals/index');
      const selectedPortalParam = {
        portalSlug: this.selectedPortalSlug,
        locale: this.selectedLocaleInPortal,
      };
      this.$store.dispatch('portals/show', selectedPortalParam);
      this.$store.dispatch('categories/index', selectedPortalParam);
      this.$store.dispatch('agents/get');
    },
    openPortalPopover() {
      this.showPortalPopover = !this.showPortalPopover;
    },
    closePortalPopover() {
      this.showPortalPopover = false;
    },
    onClickOpenAddCategoryModal() {
      this.showAddCategoryModal = true;
    },
    onClickCloseAddCategoryModal() {
      this.showAddCategoryModal = false;
    },

    // From Dashboard.vue
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    showAddLabelPopup() {
      this.showAddLabelModal = true;
    },
    hideAddLabelPopup() {
      this.showAddLabelModal = false;
    },
    openNotificationPanel() {
      this.isNotificationPanel = true;
    },
    closeNotificationPanel() {
      this.isNotificationPanel = false;
    },
    openModal(modalName) {
      if (modalName === 'NewConversationModal') {
        this.showNewConversationModal = true;
      }
      if (modalName === 'NewContactModal') {
        this.showNewContactModal = true;
      }
      if (modalName === 'AddLabel') {
        this.showAddLabelPopup();
      }
    },
    closeNewConversationModal() {
      this.showNewConversationModal = false;
    },
    closeNewContactModal() {
      this.showNewContactModal = false;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
    inboxSection() {
      if (this.hideAllInboxForAgents) {
        return {};
      }
      return {
        key: 'inboxes',
        icon: 'inbox-in-24',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    labelSection() {
      const allLabels = this.$store.getters['labels/getLabels'].filter(
        label => label.show_on_sidebar
      );
      if (!allLabels.length) return {};
      return {
        key: 'labels',
        icon: 'number-symbol',
        label: 'LABELS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        dataTestid: 'sidebar-new-label-button',
        children: allLabels.map(label => ({
          id: label.id,
          key: `label-${label.id}`,
          label: label.title,
          toState: frontendURL(
            `accounts/${this.accountId}/label/${label.title}`
          ),
          color: label.color,
          truncateLabel: true,
        })),
      };
    },
    contactLabelSection() {
      return {
        key: 'contact-labels',
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      const teams = this.$store.getters['teams/getTeams'];
      if (!teams.length) return {};
      return {
        key: 'teams',
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: teams.map(team => ({
          id: team.id,
          key: `team-${team.id}`,
          label: team.name,
          toState: frontendURL(
            `accounts/${this.accountId}/teams/${team.id}/conversations`
          ),
          truncateLabel: true,
        })),
      };
    },
    filtersSection() {
      return {
        key: 'filters',
        icon: 'folder-28',
        label: 'FILTERS',
        hasSubMenu: true,
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        key: 'segments',
        icon: 'folder-28',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    },
  },
};
</script>

<template>
  <div
    class="flex w-full h-full max-w-full min-h-0 ml-auto mr-auto app-wrapper dark:text-slate-300"
  >
    <transition name="sidebar-slide">
      <AppSidebar
        v-if="isSidebarOpen"
        class="fixed inset-y-0 left-0 z-40"
        force-expanded
        :logo-source="globalConfig.logoThumbnail"
        :installation-name="globalConfig.installationName"
        :is-a-custom-branded-instance="isACustomBrandedInstance"
        :account-id="accountId"
        :menu-items="primaryMenuItems"
        :active-menu-item="activePrimaryMenu.key"
        :secondary-menu-items="secondaryMenuItems"
        @toggleAccounts="toggleAccountModal"
        @toggleSidebar="toggleSidebar"
        @openKeyShortcutModal="toggleKeyShortcutModal"
        @openNotificationPanel="openNotificationPanel"
        @openModal="openModal"
      />
    </transition>
    <div
      v-if="isSidebarOpen"
      class="fixed inset-0 z-30 bg-black/50 md:hidden"
      @click="toggleSidebar"
    />

    <AppSidebar
      class="hidden md:flex"
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      :secondary-menu-items="secondaryMenuItems"
      @toggleAccounts="toggleAccountModal"
      @toggleSidebar="toggleSidebar"
      @openKeyShortcutModal="toggleKeyShortcutModal"
      @openNotificationPanel="openNotificationPanel"
      @openModal="openModal"
    />
    <div class="flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      <MobileHeader
        :logo-source="globalConfig.logoThumbnail"
        :installation-name="globalConfig.installationName"
        :account-id="accountId"
        @toggleSidebar="toggleSidebar"
      />
      <section class="flex flex-1 h-full min-h-0 px-0 overflow-hidden">
        <div class="flex flex-1 h-full min-h-0 overflow-hidden">
          <section
            v-if="isHelpCenterEnabled"
            class="flex flex-1 h-full min-h-0 px-0 overflow-hidden bg-surface"
          >
            <router-view @reloadLocale="fetchPortalAndItsCategories" />
            <PortalPopover
              v-if="showPortalPopover"
              :portals="portals"
              :active-portal-slug="selectedPortalSlug"
              :active-locale="selectedLocaleInPortal"
              @fetchPortal="fetchPortalAndItsCategories"
              @closePopover="closePortalPopover"
            />
            <AddCategory
              v-if="showAddCategoryModal"
              :show.sync="showAddCategoryModal"
              :portal-name="selectedPortalName"
              :locale="selectedLocaleInPortal"
              :portal-slug="selectedPortalSlug"
              @cancel="onClickCloseAddCategoryModal"
            />
          </section>
          <UpgradePage v-else />
        </div>
        <CommandBar />
        <AccountSelector
          :show-account-modal="showAccountModal"
          @closeAccountModal="toggleAccountModal"
          @showCreateAccountModal="openCreateAccountModal"
        />
        <AddAccountModal
          :show="showCreateAccountModal"
          @closeAccountCreateModal="closeCreateAccountModal"
        />
        <WootKeyShortcutModal
          :show.sync="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
        <NotificationPanel
          v-if="isNotificationPanel"
          @close="closeNotificationPanel"
        />
        <woot-modal
          :show.sync="showAddLabelModal"
          :on-close="hideAddLabelPopup"
        >
          <AddLabelModal @close="hideAddLabelPopup" />
        </woot-modal>
        <NewConversationModal
          :show="showNewConversationModal"
          @close="closeNewConversationModal"
        />
        <NewContactModal
          :show="showNewContactModal"
          @close="closeNewContactModal"
        />
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: transform 0.3s ease;
}
.sidebar-slide-enter,
.sidebar-slide-leave-to {
  transform: translateX(-100%);
}
</style>
